const getBaseURL = (): string => {
  const envURL = import.meta.env.VITE_API_BASE_URL || '';
  const isProductionBuild = import.meta.env.MODE === 'production';

  if (isProductionBuild && (envURL.includes('localhost') || envURL.includes('127.0.0.1'))) {
    return '';
  }

  if (typeof window !== 'undefined') {
    const isProductionRuntime =
      !window.location.hostname.includes('localhost') &&
      !window.location.hostname.includes('127.0.0.1');

    if (isProductionRuntime && (envURL.includes('localhost') || envURL.includes('127.0.0.1'))) {
      return '';
    }
  }

  return envURL;
};

export const API_CONFIG = {
  baseURL: getBaseURL(),
  apiVersion: '/api/v1',
} as const;

export const API_ENDPOINTS = {
  employees: '/employees',
  projects: '/projects',
  wbs: '/wbs',
  orders: '/orders',
  workingTimeModels: '/working-time-models',
  locations: '/locations',
  locationModels: '/location-models',
  location: '/location',
  employeeTimeModel: '/employee/:emp_id/time-model',
  employeeLocationModel: '/employee/:emp_id/location-model',
  employeeProjectModels: '/employee-project-models',
  projectOptionsPortfolios: '/project-options/portfolios',
  projectOptionsProcessTechnologies: '/project-options/process-technologies',
  projectOptionsDeliveryModels: '/project-options/delivery-models',
} as const;

export const getApiUrl = (endpoint: string, subPath?: string): string => {
  const url = `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}${endpoint}`;
  return subPath ? `${url}${subPath}` : url;
};

export const apiUrl = {
  employees: (id?: string) => {
    const baseUrl = getApiUrl(API_ENDPOINTS.employees);
    if (id) {
      return `${baseUrl}/${id}`;
    }
    return baseUrl;
  },
  activeEmployeesSimple: () => {
    const baseUrl = getApiUrl(API_ENDPOINTS.employees);
    return `${baseUrl}/active/simple`;
  },
  projects: (id?: string) => {
    const baseUrl = getApiUrl(API_ENDPOINTS.projects);
    if (id) {
      return `${baseUrl}/${id}`;
    }
    return baseUrl;
  },
  projectsByRole: () => {
    const baseUrl = getApiUrl(API_ENDPOINTS.projects);
    return `${baseUrl}/by-role`;
  },
  projectOrders: (projectId: string) => {
    const baseUrl = getApiUrl('/project');
    return `${baseUrl}/${projectId}/orders`;
  },
  projectOptionsPortfolios: () => getApiUrl(API_ENDPOINTS.projectOptionsPortfolios),
  projectOptionsProcessTechnologies: () =>
    getApiUrl(API_ENDPOINTS.projectOptionsProcessTechnologies),
  projectOptionsDeliveryModels: () => getApiUrl(API_ENDPOINTS.projectOptionsDeliveryModels),
  wbs: (id?: string) => {
    const baseUrl = getApiUrl(API_ENDPOINTS.wbs);
    if (id) {
      return `${baseUrl}/${id}`;
    }
    return baseUrl;
  },
  orders: (id?: string) => {
    const baseUrl = getApiUrl(API_ENDPOINTS.orders);
    if (id) {
      return `${baseUrl}/${id}`;
    }
    return baseUrl;
  },
  workingTimeModels: () => getApiUrl(API_ENDPOINTS.workingTimeModels),
  locations: () => getApiUrl(API_ENDPOINTS.locations),
  locationModels: (id?: string) => {
    const baseUrl = getApiUrl(API_ENDPOINTS.locationModels);
    if (id) {
      return `${baseUrl}/${id}`;
    }
    return baseUrl;
  },
  location: (locationId: string, path?: string) => {
    const baseUrl = getApiUrl(API_ENDPOINTS.location);
    return `${baseUrl}/${locationId}${path || ''}`;
  },
  employeeTimeModel: (empId: string) => {
    const baseUrl = getApiUrl(API_ENDPOINTS.employeeTimeModel);
    return baseUrl.replace(':emp_id', empId);
  },
  employeeLocationModel: (empId: string) => {
    const baseUrl = getApiUrl(API_ENDPOINTS.employeeLocationModel);
    return baseUrl.replace(':emp_id', empId);
  },
  employeeProjectModels: (id?: string) => {
    const baseUrl = getApiUrl(API_ENDPOINTS.employeeProjectModels);
    if (id) {
      return `${baseUrl}/${id}`;
    }
    return baseUrl;
  },
  projectReportSettings: (projectNumber?: string) => {
    const baseUrl = `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/project-report-settings`;
    if (projectNumber) {
      return `${baseUrl}/${projectNumber}`;
    }
    return baseUrl;
  },
  costGroups: (id?: string) => {
    const baseUrl = `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/cost-groups`;
    if (id) return `${baseUrl}/${id}`;
    return baseUrl;
  },
  costGroupYearRates: (groupId: string, yid?: string) => {
    const baseUrl = `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/cost-groups/${groupId}/year-rates`;
    if (yid) return `${baseUrl}/${yid}`;
    return baseUrl;
  },
  costGroupAssignments: () =>
    `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/cost-group-assignments`,
  employeeCostGroupAssignments: (employeeId: string, aid?: string) => {
    const baseUrl = `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/employees/${employeeId}/cost-group-assignments`;
    if (aid) return `${baseUrl}/${aid}`;
    return baseUrl;
  },
  organisationStructure: (id?: string | number) => {
    const baseUrl = `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/organisation-structure`;
    if (id != null && id !== '') return `${baseUrl}/${id}`;
    return baseUrl;
  },
  vendors: (id?: string | number) => {
    const baseUrl = `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/vendors`;
    if (id != null && id !== '') return `${baseUrl}/${id}`;
    return baseUrl;
  },
  appRoles: (id?: string) => {
    const baseUrl = `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/app-roles`;
    if (id != null && id !== '') return `${baseUrl}/${id}`;
    return baseUrl;
  },
  customers: (id?: string | number) => {
    const baseUrl = `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/customers`;
    if (id != null && id !== '') return `${baseUrl}/${id}`;
    return baseUrl;
  },
  wbsDetails: (id?: string) => {
    const baseUrl = `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/wbs-details`;
    if (id != null && id !== '') return `${baseUrl}/${encodeURIComponent(id)}`;
    return baseUrl;
  },
  projectProperties: () => `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/project-properties`,
};
